/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import {
  ROUTES,
  HIERARCHY_ITEM_ROUTE_PATHS,
  MONITOR_PAGE_URLS,
  SEPARATOR,
} from '../../Common/constants/constants';
import { RESOURCE_TYPES, HIERARCHY_ITEM_STATUS } from '../constants/constants';

import { HierarchiesSequence, DetailsURLMatchType, DeviceDtoType } from 'HierarchyTypes';
import { CommonHierarchyResource } from 'HierarchyActionTypes';

// eslint-disable-next-line consistent-return
export const findNestedRowObject = (rowId: string, data, parent, idx: number): any => {
  // data match
  if (data.id === rowId) {
    return { data, parent, idx };
  }
  // check children
  if (data.children && data.children.length > 0) {
    return data.children
      .map((i, index) => findNestedRowObject(rowId, i, data, index))
      .filter(i => i);
  }
};

/**
 * Function to place the new sequence/uuid into the rowId convention for the table
 * @param {string} rowId The row id from the table
 * @param {string} sequence The current hierarchy level
 * @param {object} item The current item being placed in the table
 * @returns new row id
 */
export const getRowIdWithNextSequence = (
  rowId: string,
  sequence: string,
  item: CommonHierarchyResource
): string => {
  const indexOfSequence = rowId.indexOf(sequence);
  if (indexOfSequence > -1) {
    return `${rowId.slice(0, indexOfSequence - 2)}${SEPARATOR}${sequence}${SEPARATOR}${item.uuid}`;
  }
  return `${rowId}${SEPARATOR}${sequence}${SEPARATOR}${item.uuid}`;
};

export const getNextHierarchySequence = (
  rowId: string,
  shouldPreserveHierarchyLevel = false // Whether to stick to the current hierarchy sequence level
): string => {
  const reverseSequence = Object.keys(HierarchiesSequence).reverse();
  const nextSequenceIndex = reverseSequence.findIndex(sequence => rowId.includes(sequence));

  return shouldPreserveHierarchyLevel
    ? reverseSequence[nextSequenceIndex]
    : reverseSequence[nextSequenceIndex - 1]; // Use the next one
};

export const tableDataToBeUpdated = (
  tableData,
  rowId: string,
  hasLoadMore = false,
  newRowData,
  replaceLoading = true,
  addingData = false
): any => {
  // we may need have more than one item in data, eg sub assets at locations and an asset as subasset
  const data = tableData
    .map((i, idx) => findNestedRowObject(rowId, i, i, idx))

    .map(i => (Array.isArray(i) ? i.flat(Infinity) : [i]))[0];

  // if we should replace the loading state
  const returnData = (updateWithNewChildData, itemData, table): any => {
    if (itemData.parent.id === itemData.data.id) {
      return table.map((i, idx) => (idx === itemData.idx ? updateWithNewChildData : i));
    }

    // eslint-disable-next-line no-param-reassign
    itemData.parent.children[itemData.idx] = updateWithNewChildData;
    return table.map(i => (i.id === itemData.parent.id ? itemData.parent : i));
  };

  const generateChildDataAtTable = (childData, table) => {
    if (replaceLoading && childData.data.children?.find(i => i.id.includes('-loading'))) {
      const updateWithNewChildData = {
        ...childData.data,
        hasLoadMore,
        children: newRowData,
      };

      return returnData(updateWithNewChildData, childData, table);
    }
    if (addingData) {
      // for update data, usually current + new
      return returnData(newRowData, childData, table);
    }
    return tableData;
  };

  // keep reference for recursive update table data based on items loading found
  let table = tableData;
  data.forEach(i => {
    table = generateChildDataAtTable(i, table);
  });

  return table;
};

/**
 *
 * @param {string} hierarchyPath hierarchy path chunk for a given item in the hierarchy
 * @returns {string} the route path or API patch chunk based on the hierarchy level (without the system chunk for path with the following format /sites/siteId/systems/systemId/locations/locationId)
 */
export const stripSystemChunkFromLocationHierarchyPath = (hierarchyPath: string): string => {
  if (
    hierarchyPath.includes('/sites/') &&
    hierarchyPath.includes('/systems/') &&
    hierarchyPath.includes('/locations/')
  ) {
    return hierarchyPath
      .split('/systems')[0]
      .concat('/locations')
      .concat(hierarchyPath.split('/locations')[1]);
  }
  return hierarchyPath;
};

export const stripDevicesFromPath = (hierarchyPath: string): string =>
  !hierarchyPath.includes('/devices/')
    ? hierarchyPath
    : // devices should only request from the deviceType
      hierarchyPath.split('/devices/')[0];

/**
 *
 * @param {object} matchParam an object that contains route parameters, only a subset of the parameters will exist at the same time, depending on the route
 * @returns {string} the route path or API patch chunk based on the hierarchy level
 */
export const getPathFromMatchObject = ({
  organizationId,
  siteId,
  systemId,
  locationId,
  locationTypeId,
  assetId,
  assetTypeId,
  deviceTypeId,
  deviceId,
}: DetailsURLMatchType): string => {
  if (organizationId) {
    return `/organizations/${organizationId}`;
  }
  if (siteId) {
    let path = `/sites/${siteId}`;
    if (systemId) {
      path += `/systems/${systemId}`;
      if (locationId) {
        path += `/${'locations'}/${locationId}`;
      } else if (assetId) {
        path += `/${'assets'}/${assetId}`;
      }
    } else if (locationId) {
      path += `/${'locations'}/${locationId}`;
    } else if (assetId) {
      path += `/${'assets'}/${assetId}`;
    }
    return path;
  }
  if (locationTypeId) {
    let path = `/${'locationTypes'}/${locationTypeId}`;
    if (locationId) {
      path += `/${'locations'}/${locationId}`;
    }
    return path;
  }
  if (assetTypeId) {
    let path = `/assetTypes/${assetTypeId}`;
    if (assetId) {
      path += `/assets/${assetId}`;
    }
    return path;
  }
  if (deviceTypeId) {
    let path = `/deviceTypes/${deviceTypeId}`;
    if (deviceId) {
      path += `/devices/${deviceId}`;
    }
    return path;
  }
  // Should never end up here!
  return '';
};

/**
 * Returns the hierarchyPath based on the hierarchy type
 * @param {object} matchParam an object that contains route parameters, only a subset of the parameters will exist at the same time, depending on the route
 * @param {string} type
 * @returns {string} the route path
 */
export const getHierarchyPathBasedOnType = (
  {
    deviceTypeId,
    locationId,
    systemId,
    siteId,
    organizationId,
    assetId,
    deviceId,
  }: DetailsURLMatchType,
  type: string
): string => {
  switch (type) {
    case HIERARCHY_ITEM_ROUTE_PATHS.devices:
      return `/deviceTypes/${deviceTypeId}/devices/${deviceId}`;
    case HIERARCHY_ITEM_ROUTE_PATHS.deviceTypes:
      return `/deviceTypes/${deviceTypeId}`;
    case HIERARCHY_ITEM_ROUTE_PATHS.locations:
      return `/sites/${siteId}/locations/${locationId}`;
    case HIERARCHY_ITEM_ROUTE_PATHS.systems:
      return `/sites/${siteId}/systems/${systemId}`;
    case HIERARCHY_ITEM_ROUTE_PATHS.assets:
      return `/sites/${siteId}/assets/${assetId}`;
    case HIERARCHY_ITEM_ROUTE_PATHS.sites:
      return `/sites/${siteId}`;
    case HIERARCHY_ITEM_ROUTE_PATHS.organizations:
      return `/organizations/${organizationId}`;
    default:
      return '';
  }
};

// TODO: Temporary function so that APIs that still don't support hierarchy path could at least still work with an entityTypeId

/**
 *
 * @param {object} matchParam an object that contains route parameters, only a subset of the parameters will exist at the same time, depending on the route
 * @returns {string} the value of the leaf item in the matchParam object
 */
export const getLeafIdFromMatchObject = ({
  organizationId,
  siteId,
  systemId,
  locationId,
  locationTypeId,
  assetId,
  assetTypeId,
  deviceTypeId,
  deviceId,
  workspaceId,
}: DetailsURLMatchType): string => {
  if (deviceId) {
    return deviceId;
  }
  if (deviceTypeId) {
    return deviceTypeId;
  }
  if (assetId) {
    return assetId;
  }
  if (assetTypeId) {
    return assetTypeId;
  }
  if (locationId) {
    return locationId;
  }
  if (locationTypeId) {
    return locationTypeId;
  }
  if (systemId) {
    return systemId;
  }
  if (siteId) {
    return siteId;
  }
  if (organizationId) {
    return organizationId;
  }
  // Should never end up here!
  return workspaceId;
};

/**
 *
 * @param {string} hierarchyPath hierarchy path chunk for a given item in the hierarchy
 * @returns {string} the value of the leaf item in hierarchyPath
 */
export const getLeafIdFromHierarchyPath = (hierarchyPath: string): string =>
  hierarchyPath?.split('/')?.pop() ?? '';

/**
 *
 * @param {string} hierarchyPath hierarchy path chunk for a given item in the hierarchy
 * @returns {boolean} whether or not hierarchyPath is a device type path
 */
export const isHierarchyPathADeviceType = (hierarchyPath: string): boolean =>
  hierarchyPath?.startsWith('/deviceType') ?? false;

export const getDeviceTypeFromHierarchyPath = (hierarchyPath: string): string =>
  hierarchyPath?.split('/deviceTypes/')?.pop()?.split('/')?.[0] ?? '';

export const getSiteFromHierarchyPath = (hierarchyPath: string): string =>
  hierarchyPath?.split('/sites/')?.pop()?.split('/')?.[0] ?? '';

/**
 *
 * @param {string} rowId The rowId from the HierarchyTable. (ex: 'WORKSPACE--AnalyticsServiceDev--ORGANIZATION--e813e339-d5ef'...etc)
 * @returns {object} An object to access the hierachy properties easier
 */
export const parseHierarchyTableRowId = (
  rowId: string,
  isSetup?: boolean
): {
  WORKSPACE: string;
  ORGANIZATION: string;
  SITE: string;
  SYSTEM: string;
  LOCATION: string;
  SUBLOCATION: string;
  DEVICE_TYPE: string;
  DEVICE: string;
  ASSET: string;
  SUBASSET: string;
} => {
  const rowData = rowId.split(SEPARATOR);
  const hasSubLocation = rowData.includes(HierarchiesSequence.SUBLOCATION);
  const hasAsset = rowData.includes(HierarchiesSequence.ASSET);
  return {
    WORKSPACE: rowData[1],
    ORGANIZATION: rowData[3],
    SITE: rowData[5],
    SYSTEM: rowData[7],
    LOCATION: rowData[9],
    SUBLOCATION: isSetup || hasSubLocation ? rowData[11] : null,
    ASSET: hasSubLocation ? rowData[13] : rowData[11],
    SUBASSET: hasSubLocation ? rowData[15] : rowData[13],
    DEVICE_TYPE: hasSubLocation || hasAsset ? rowData[13] : rowData[11],
    DEVICE: hasSubLocation || hasAsset ? rowData[15] : rowData[13],
  };
};

/**
 *
 * @param {object} matchParam an object that contains route parameters, only a subset of the parameters will exist at the same time, depending on the route
 * @returns {string} the type of hierarchy item
 */
export const getHierarchyItemTypeFromMatchObject = (
  {
    organizationId,
    siteId,
    systemId,
    locationId,
    locationTypeId,
    assetId,
    assetTypeId,
    deviceTypeId,
    deviceId,
  }: DetailsURLMatchType,
  returnForUrl?: boolean
): string => {
  if (deviceId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.devices : RESOURCE_TYPES.DEVICE;
  }
  if (deviceTypeId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.deviceTypes : RESOURCE_TYPES.DEVICE_TYPE;
  }
  if (assetId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.assets : RESOURCE_TYPES.ASSET;
  }
  if (assetTypeId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.assetTypes : RESOURCE_TYPES.ASSET_TYPE;
  }
  if (locationId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.locations : RESOURCE_TYPES.LOCATION;
  }
  if (locationTypeId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.locationTypes : RESOURCE_TYPES.LOCATION_TYPE;
  }
  if (systemId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.systems : RESOURCE_TYPES.SYSTEM;
  }
  if (siteId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.sites : RESOURCE_TYPES.SITE;
  }
  if (organizationId) {
    return returnForUrl ? HIERARCHY_ITEM_ROUTE_PATHS.organizations : RESOURCE_TYPES.ORGANIZATION;
  }
  // Should never end up here!
  return 'workspace';
};

/**
 *
 * @param {string} matchParam an object that contains route parameters, only a subset of the parameters will exist at the same time, depending on the route
 * @returns {string} the type of hierarchy item
 */
export const getHierarchyItemTypeFromHierarchyPath = (hierarchyPath: string): string => {
  if (hierarchyPath.includes('/devices/')) {
    return RESOURCE_TYPES.DEVICE;
  }
  if (hierarchyPath.includes('/deviceTypes/')) {
    return RESOURCE_TYPES.DEVICE_TYPE;
  }
  if (hierarchyPath.includes('/assets/')) {
    return RESOURCE_TYPES.ASSET;
  }
  if (hierarchyPath.includes('/assetTypes/')) {
    return RESOURCE_TYPES.ASSET;
  }
  if (hierarchyPath.includes('/locations/')) {
    return RESOURCE_TYPES.LOCATION;
  }
  if (hierarchyPath.includes('/locationTypes/')) {
    return RESOURCE_TYPES.LOCATION_TYPE;
  }
  if (hierarchyPath.includes('/systems/')) {
    return RESOURCE_TYPES.SYSTEM;
  }
  if (hierarchyPath.includes('/sites/')) {
    return RESOURCE_TYPES.SITE;
  }
  if (hierarchyPath.includes('/organizations/')) {
    return RESOURCE_TYPES.ORGANIZATION;
  }
  // Should never end up here!
  return '';
};

/**
 *
 * @param {object} hierarchyItemObject an object that contains a type and a uuid property (if type is LOCATION or SYSTEM, the object needs to also have siteUUId)
 * @returns {string} the hierarchy path
 */
export const getHierarchyPathFromObject = (hierarchyItemObject: {
  type: string;
  uuid: string;
  siteUUId: string;
}): string => {
  const { type, uuid, siteUUId } = hierarchyItemObject;

  switch (type) {
    case RESOURCE_TYPES.ORGANIZATION:
      return `/organizations/${uuid}`;

    case RESOURCE_TYPES.SITE:
      return `/sites/${uuid}`;

    case RESOURCE_TYPES.SYSTEM:
      return `/sites/${siteUUId}/systems/${uuid}`;

    case RESOURCE_TYPES.LOCATION:
      return `/sites/${siteUUId}/locations/${uuid}`;

    case RESOURCE_TYPES.ASSET:
      return `/sites/${siteUUId}/assets/${uuid}`;

    case RESOURCE_TYPES.DEVICE_TYPE:
      return `/deviceTypes/${uuid}`;

    case RESOURCE_TYPES.LOCATION_TYPE:
      return `/locationTypes/${uuid}`;

    default:
      return uuid;
  }
};
export const getSetupToPath = (path: string): string =>
  path.includes('sites') && path.includes('systems')
    ? `${ROUTES.SETUP_HIERARCHY}${path}`
    : `${ROUTES.SETUP}${path}`;

/**
 * Function that returns path API of the dashboard to load
 * @param id - row id eg. WORKSPACE--analytics--ORGANIZATION--eokd9382-292
 * @returns object with path api and type to be used in reducer
 */
export const hierarchyDashboardPath = (
  id: string
): { path: string; type: string; uuid: string } => {
  const { ORGANIZATION, SITE, SYSTEM, LOCATION, SUBLOCATION, DEVICE, DEVICE_TYPE, ASSET } =
    parseHierarchyTableRowId(id);

  const { path, type, uuid } =
    DEVICE_TYPE && DEVICE
      ? {
          path: `/deviceTypes/${DEVICE_TYPE}/devices/${DEVICE}`,
          type: RESOURCE_TYPES.DEVICE,
          uuid: DEVICE,
        } // /deviceTypes/{deviceTypeId}/devices/{deviceId}
      : ASSET
      ? { path: `/sites/${SITE}/assets/${ASSET}`, type: RESOURCE_TYPES.ASSET, uuid: ASSET } // /sites/{siteId}/assets/{assetId}
      : SUBLOCATION
      ? {
          path: `/sites/${SITE}/locations/${SUBLOCATION}`,
          type: RESOURCE_TYPES.LOCATION,
          uuid: SUBLOCATION,
        } // /sites/{siteId}/locations/{locationId}
      : LOCATION
      ? {
          path: `/sites/${SITE}/locations/${LOCATION}`,
          type: RESOURCE_TYPES.LOCATION,
          uuid: LOCATION,
        }
      : SYSTEM
      ? { path: `/sites/${SITE}/systems/${SYSTEM}`, type: RESOURCE_TYPES.SYSTEM, uuid: SYSTEM } // /sites/{siteId}/systems/{systemId}
      : SITE
      ? { path: `/sites/${SITE}`, type: RESOURCE_TYPES.SITE, uuid: SITE } // /sites/{siteId}
      : ORGANIZATION
      ? {
          path: `/organizations/${ORGANIZATION}`,
          type: RESOURCE_TYPES.ORGANIZATION,
          uuid: ORGANIZATION,
        } // /organizations/{organizationId}
      : { path: undefined, type: undefined, uuid: undefined };

  return { path, type, uuid };
};

/**
 * Function that defines the row link click  route to be pushed
 */
export const handleHierarchyLinkClick = (rowId: string, isSetup = true): string => {
  const hierarchySeqValue =
    Object.keys(HierarchiesSequence).reverse()[
      Object.keys(HierarchiesSequence)
        .reverse()
        .findIndex(i => rowId.includes(i))
    ];
  const {
    WORKSPACE,
    ORGANIZATION,
    SITE,
    SYSTEM,
    LOCATION,
    SUBLOCATION,
    DEVICE_TYPE,
    DEVICE,
    ASSET,
    SUBASSET,
  } = parseHierarchyTableRowId(rowId, isSetup);

  switch (hierarchySeqValue) {
    case HierarchiesSequence.WORKSPACE: {
      // url setup - /workspace/:workspaceId
      // url monitor - /workspace/:workspaceId/organization/:orgId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${MONITOR_PAGE_URLS.DASHBOARDS}`;
    }
    case HierarchiesSequence.ORGANIZATION: {
      // url setup - /organization/:orgId
      // url monitor - /workspace/:workspaceId/organization/:orgId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.organizations}/${ORGANIZATION}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${HIERARCHY_ITEM_ROUTE_PATHS.organizations}/${ORGANIZATION}/${MONITOR_PAGE_URLS.DASHBOARDS}`;
    }
    case HierarchiesSequence.SITE: {
      // url setup - sites/:siteId
      // url monitor - /workspace/:workspaceId/organization/:orgId/sites/:siteId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${HIERARCHY_ITEM_ROUTE_PATHS.organizations}/${ORGANIZATION}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${MONITOR_PAGE_URLS.DASHBOARDS}`;
    }
    case HierarchiesSequence.SYSTEM: {
      // url setup - sites/:siteId/systems/:systemId
      // url monitor -  /workspace/:workspaceId/organization/:orgId/sites/:siteId/systems/:systemId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${HIERARCHY_ITEM_ROUTE_PATHS.systems}/${SYSTEM}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${HIERARCHY_ITEM_ROUTE_PATHS.organizations}/${ORGANIZATION}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${HIERARCHY_ITEM_ROUTE_PATHS.systems}/${SYSTEM}/${MONITOR_PAGE_URLS.DASHBOARDS}`;
    }
    case HierarchiesSequence.LOCATION: {
      // url setup - sites/:siteId/systems/:system/locations/:locationId/details
      // url monitor - /workspace/:workspaceId/organization/:orgId/sites/:siteId/systems/:systemId/locations/:locationId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.systems
          }/${SYSTEM}/${HIERARCHY_ITEM_ROUTE_PATHS.locations}/${SUBLOCATION || LOCATION}/${
            ROUTES.SETUP_HIERARCHY_DETAILS
          }`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.organizations
          }/${ORGANIZATION}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.systems
          }/${SYSTEM}/${HIERARCHY_ITEM_ROUTE_PATHS.locations}/${SUBLOCATION || LOCATION}/${
            MONITOR_PAGE_URLS.DASHBOARDS
          }`;
    }
    case HierarchiesSequence.SUBLOCATION: {
      // sublocations are also treated as locations
      // url setup - sites/:siteId/systems/:systemId/locations/:locationId
      // url monitor - /workspace/:workspaceId/organization/:orgId/sites/:siteId/systems/:systemId/locations/:locationId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${HIERARCHY_ITEM_ROUTE_PATHS.systems}/${SYSTEM}/${HIERARCHY_ITEM_ROUTE_PATHS.locations}/${SUBLOCATION}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${HIERARCHY_ITEM_ROUTE_PATHS.organizations}/${ORGANIZATION}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${HIERARCHY_ITEM_ROUTE_PATHS.systems}/${SYSTEM}/${HIERARCHY_ITEM_ROUTE_PATHS.locations}/${SUBLOCATION}/${MONITOR_PAGE_URLS.DASHBOARDS}`;
    }
    case HierarchiesSequence.DEVICE: {
      // url setup - /deviceTypes/:deviceTypeId/devices/:deviceId
      // url monitor - /workspace/:workspaceId/organization/:orgId/sites/:siteId/systems/:systemId/locations/:locationId/deviceTypes/:deviceTypeId/devices/:deviceId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.deviceTypes}/${DEVICE_TYPE}/${HIERARCHY_ITEM_ROUTE_PATHS.devices}/${DEVICE}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.organizations
          }/${ORGANIZATION}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.systems
          }/${SYSTEM}/${HIERARCHY_ITEM_ROUTE_PATHS.locations}/${SUBLOCATION || LOCATION}/${
            HIERARCHY_ITEM_ROUTE_PATHS.deviceTypes
          }/${DEVICE_TYPE}/${HIERARCHY_ITEM_ROUTE_PATHS.devices}/${DEVICE}/${
            MONITOR_PAGE_URLS.DASHBOARDS
          }`;
    }
    case HierarchiesSequence.ASSET: {
      // url setup - /assetType/:assetType/asset/:asset
      // url monitor - /workspace/:workspaceId/organization/:orgId/sites/:siteId/systems/:systemId/locations/:locationId/assetType/:assetTypeID/asset/:assetId
      return isSetup
        ? `${ROUTES.SETUP_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.assets
          }/${SUBASSET || ASSET}/${ROUTES.SETUP_HIERARCHY_DETAILS}`
        : `${ROUTES.MONITOR_HIERARCHY}/${HIERARCHY_ITEM_ROUTE_PATHS.workspace}/${WORKSPACE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.organizations
          }/${ORGANIZATION}/${HIERARCHY_ITEM_ROUTE_PATHS.sites}/${SITE}/${
            HIERARCHY_ITEM_ROUTE_PATHS.systems
          }/${SYSTEM}/${HIERARCHY_ITEM_ROUTE_PATHS.locations}/${SUBLOCATION || LOCATION}/${
            HIERARCHY_ITEM_ROUTE_PATHS.assets
          }/${SUBASSET || ASSET}/${MONITOR_PAGE_URLS.DASHBOARDS}`;
    }
    default:
      return isSetup ? ROUTES.SETUP_HIERARCHY : ROUTES.MONITOR;
  }
};

/**
 * Function that converts a HierarchyTable rowId string into an API path (hierarchyPath) that can be used to call the /archive API for example
 * @param rowId - a HierarchyTable rowId (example: WORKSPACE--AnalyticsServiceDev--ORGANIZATION--ea6bdf85-f61f-44f3-9cda-fcdce6a64eaf--SITE--19b04462-c972-4ef4-ad01-9ea7652f8938)
 * @returns a hierarchy path extracted from the rowId (example: /sites/19b04462-c972-4ef4-ad01-9ea7652f8938)
 */
export const convertHierarchiesTableRowIdToAPIPath = (rowId: string): string =>
  stripSystemChunkFromLocationHierarchyPath(
    handleHierarchyLinkClick(rowId).split(ROUTES.SETUP_HIERARCHY).pop().split('/details')[0]
  );

/**
 * Function that returns selected rowId based on the current url path
 * @param tenantId - tenant id string
 * @param url - urls match types
 * @returns rowId string
 */
export const rowIdBasedOnURLPath = (
  tenantId: string,
  {
    workspaceId,
    organizationId,
    siteId,
    systemId,
    locationId,
    assetId,
    deviceTypeId,
    deviceId,
  }: DetailsURLMatchType,
  resourceUUId?: string
): string => {
  if (deviceId) {
    return `device:${deviceTypeId}:${deviceId}${SEPARATOR}${resourceUUId}`;
  }
  if (deviceTypeId) {
    //  return `device:${deviceTypeId}:${deviceId}${SEPARATOR}${resourceUUId}`;
    return `deviceType:${deviceTypeId}${SEPARATOR}${resourceUUId}`;
  }
  if (assetId) {
    return `${HierarchiesSequence.WORKSPACE}${SEPARATOR}${tenantId}${SEPARATOR}${HierarchiesSequence.ORGANIZATION}${SEPARATOR}${organizationId}${SEPARATOR}${HierarchiesSequence.SITE}${SEPARATOR}${siteId}${SEPARATOR}${HierarchiesSequence.SYSTEM}${SEPARATOR}${systemId}${SEPARATOR}${HierarchiesSequence.LOCATION}${SEPARATOR}${locationId}${SEPARATOR}${HierarchiesSequence.ASSET}${SEPARATOR}${assetId}`;
  }
  if (locationId) {
    return `${HierarchiesSequence.WORKSPACE}${SEPARATOR}${tenantId}${SEPARATOR}${HierarchiesSequence.ORGANIZATION}${SEPARATOR}${organizationId}${SEPARATOR}${HierarchiesSequence.SITE}${SEPARATOR}${siteId}${SEPARATOR}${HierarchiesSequence.SYSTEM}${SEPARATOR}${systemId}${SEPARATOR}${HierarchiesSequence.LOCATION}${SEPARATOR}${locationId}`;
  }
  if (systemId) {
    return `${HierarchiesSequence.WORKSPACE}${SEPARATOR}${tenantId}${SEPARATOR}${HierarchiesSequence.ORGANIZATION}${SEPARATOR}${organizationId}${SEPARATOR}${HierarchiesSequence.SITE}${SEPARATOR}${siteId}${SEPARATOR}${HierarchiesSequence.SYSTEM}${SEPARATOR}${systemId}`;
  }
  if (siteId) {
    return `${HierarchiesSequence.WORKSPACE}${SEPARATOR}${tenantId}${SEPARATOR}${HierarchiesSequence.ORGANIZATION}${SEPARATOR}${organizationId}${SEPARATOR}${HierarchiesSequence.SITE}${SEPARATOR}${siteId}`;
  }
  if (organizationId) {
    return `${HierarchiesSequence.WORKSPACE}${SEPARATOR}${tenantId}${SEPARATOR}${HierarchiesSequence.ORGANIZATION}${SEPARATOR}${organizationId}`;
  }
  if (workspaceId) {
    return `${HierarchiesSequence.WORKSPACE}${SEPARATOR}${tenantId}`;
  }
  return null;
};

export const isHierarchyPathADevice = (hierarchyPath: string): boolean =>
  getHierarchyItemTypeFromHierarchyPath(hierarchyPath) === RESOURCE_TYPES.DEVICE ?? false;

export const getDeviceIdFromHierarchyPath = (hierarchyPath: string): string =>
  isHierarchyPathADevice(hierarchyPath) ? getLeafIdFromHierarchyPath(hierarchyPath) : null;

export const tableDataChildWithArchivedItem = (tableDataChild, hierarchyPath: string): any => {
  const hierarchyItemType = getHierarchyItemTypeFromHierarchyPath(hierarchyPath);
  const itemToBeRestoredFound = tableDataChild.id.endsWith(
    `${hierarchyItemType}${SEPARATOR}${getLeafIdFromHierarchyPath(hierarchyPath)}`
  );
  return {
    ...tableDataChild,
    values: itemToBeRestoredFound
      ? { ...tableDataChild.values, status: HIERARCHY_ITEM_STATUS.ARCHIVED }
      : tableDataChild.values,
    children: tableDataChild?.children?.map(child =>
      tableDataChildWithArchivedItem(child, hierarchyPath)
    ),
  };
};

export const tableDataWithArchivedItem = (tableData, archivedItemHierarchyPath: string): any =>
  tableData.map(data => ({
    ...data,
    ...tableDataChildWithArchivedItem(data, archivedItemHierarchyPath),
  }));

export const tableDataChildWithRestoredItem = (tableDataChild, hierarchyPath: string): any => {
  const hierarchyItemType = getHierarchyItemTypeFromHierarchyPath(hierarchyPath);
  const itemToBeRestoredFound = tableDataChild.id.endsWith(
    `${hierarchyItemType}${SEPARATOR}${getLeafIdFromHierarchyPath(hierarchyPath)}`
  );
  return {
    ...tableDataChild,
    values: itemToBeRestoredFound
      ? { ...tableDataChild.values, status: HIERARCHY_ITEM_STATUS.ACTIVE }
      : tableDataChild.values,
    children: tableDataChild?.children?.map(child =>
      tableDataChildWithRestoredItem(child, hierarchyPath)
    ),
  };
};

export const tableDataWithRestoredItem = (tableData, restoredItemHierarchyPath: string): any =>
  tableData.map(data => ({
    ...data,
    ...tableDataChildWithRestoredItem(data, restoredItemHierarchyPath),
  }));

export const getTableDataChildWithActiveItems = (tableDataChild): any => ({
  ...tableDataChild,
  children: tableDataChild?.children
    ?.filter(
      child => !child?.values?.status || child?.values?.status === HIERARCHY_ITEM_STATUS.ACTIVE
    )
    ?.map(child => getTableDataChildWithActiveItems(child)),
});

export const getTableDataWithActiveItems = (tableData): any =>
  tableData.map(data => ({
    ...data,
    ...getTableDataChildWithActiveItems(data),
  }));

export const getHierarchiesTableChildNestedRowByRowId = (tableDataChild, rowId: string): any => {
  if (tableDataChild.id === rowId) {
    return tableDataChild;
  }
  if (tableDataChild.children) {
    let childFound = null;
    tableDataChild.children.find(child => {
      childFound = getHierarchiesTableChildNestedRowByRowId(child, rowId);
      return childFound;
    });
    return childFound;
  }
  return null;
};

export const getHierarchiesTableNestedRowByRowId = (tableData, rowId: string): any => {
  let rowData = null;
  tableData.find(data => {
    rowData = getHierarchiesTableChildNestedRowByRowId(data, rowId);
    return rowData;
  });
  return rowData;
};

/**
 * Compute and return name and description of a device
 * @param device - DeviceDtoType
 * @returns tuple [string, string]
 */
export const getDevicePresentation = ({
  deviceTypeName,
  deviceIdentifier,
  name,
  description,
}: DeviceDtoType): [string, string] => {
  const deviceId = deviceIdentifier ?? name;

  const id = deviceTypeName ? `${deviceTypeName}: ${deviceId}` : deviceId;

  return [id, description || null];
};
